import { useEffect, useRef, useState, type ReactNode } from "react";

/**
 * A button that opens a list of actions.
 *
 * The session toolbar has more actions than fit across a laptop screen, and
 * most are used rarely. Grouping them keeps the few that are used constantly -
 * control, quality, screens - on the bar itself.
 */
export interface MenuItem {
  label: string;
  onSelect?: () => void;
  /** Shown to the right, e.g. a shortcut or current state. */
  hint?: string;
  disabled?: boolean;
  /** Renders a tick, for items that toggle something. */
  checked?: boolean;
  /** Draws a rule above this item. */
  separated?: boolean;
  danger?: boolean;
}

export function Menu({
  label,
  items,
  align = "left",
  title,
}: {
  label: ReactNode;
  items: MenuItem[];
  align?: "left" | "right";
  title?: string;
}) {
  const [open, setOpen] = useState(false);
  const root = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (!root.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    // Capture: the session canvas swallows events on the way down.
    document.addEventListener("mousedown", onDown, true);
    document.addEventListener("keydown", onKey, true);
    return () => {
      document.removeEventListener("mousedown", onDown, true);
      document.removeEventListener("keydown", onKey, true);
    };
  }, [open]);

  return (
    <div className="menu" ref={root}>
      <button
        className="btn btn--sm"
        aria-haspopup="menu"
        aria-expanded={open}
        title={title}
        onClick={() => setOpen((v) => !v)}
      >
        {label} <span aria-hidden="true">&#9662;</span>
      </button>
      {open ? (
        <div className="menu__list" role="menu" data-align={align}>
          {items.map((item, i) => (
            <button
              key={i}
              role="menuitem"
              className="menu__item"
              data-danger={item.danger || undefined}
              data-separated={item.separated || undefined}
              disabled={item.disabled}
              onClick={() => {
                setOpen(false);
                item.onSelect?.();
              }}
            >
              <span className="menu__tick" aria-hidden="true">{item.checked ? "✓" : ""}</span>
              <span className="menu__label">{item.label}</span>
              {item.hint ? <span className="menu__hint">{item.hint}</span> : null}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}
